import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import VideoGrid from './VideoGrid';
import ChannelPage from './ChannelPage';
import StudioPage from './StudioPage';
import HistoryPage from './HistoryPage';
import AuthPage from './AuthPage';
import SpeedoTubeUploadModal from './SpeedoTubeUploadModal';
import AlbumsPage from './AlbumsPage';
import WatchPage from './WatchPage';
import ShortsPage from './ShortsPage';
import WatchLaterPage from './WatchLaterPage';
import LikedVideosPage from './LikedVideosPage';
import PlaylistsPage from './PlaylistsPage';
import YourVideosPage from './YourVideosPage';
import DownloadsPage from './DownloadsPage';
import SearchResultsPage from './SearchResultsPage';
import MusicMiniPlayer from './MusicMiniPlayer';
import Footer from './Footer';
import Toast from './Toast';
import { MdInfoOutline, MdHome, MdOutlineAlbum, MdAdd } from 'react-icons/md';
import { HiOutlineUserCircle, HiOutlineUser } from 'react-icons/hi2';
import { SiYoutubekids } from 'react-icons/si';
import { getTranslation } from './translations';
import { getAvatarColor } from './utils/avatarUtils';
import { getActiveAccount, hydrateAccount } from './utils/accountUtils';
import { uploadChannelVideo, fetchChannelCustomization } from './api/studioApi';
import { API_BASE_URL, normalizeMediaUrl } from './config';

export const isWatchPage = (path) =>
  Boolean(path && (path === '/watch' || path.startsWith('/watch?') || path.startsWith('/watch/')));

export default function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth <= 1312;
  });
  const [windowWidth, setWindowWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1200));
  const isMobileOrTablet = windowWidth <= 792;

  // Mobile / Tablet Drawer State
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobileDrawerClosing, setIsMobileDrawerClosing] = useState(false);

  const [watchDrawerOpen, setWatchDrawerOpen] = useState(false);
  const [isDrawerClosing, setIsDrawerClosing] = useState(false);

  useEffect(() => {
    let timeoutId = null;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 60);
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [watchVideoId, setWatchVideoId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('v') || null;
  });
  const [activePath, setActivePath] = useState(() => {
    const path = window.location.pathname || '/';
    const search = window.location.search || '';

    // If currently on watch page or URL has video parameter ?v= or album parameter ?album=
    if (isWatchPage(path) || search.includes('v=') || search.includes('album=')) {
      return '/watch' + search;
    }

    // Direct browser URL routes: reload strictly on that page
    if (path && path !== '/') {
      return path;
    }

    // Default to Home '/'
    return '/';
  });
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadModalVideoType, setUploadModalVideoType] = useState(null);

  // Persistent User Profile State initialized from localStorage
  const [userProfile, setUserProfile] = useState(() => {
    if (localStorage.getItem('speedotube_logged_out') === 'true') {
      return null;
    }
    return getActiveAccount();
  });

  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    if (localStorage.getItem('speedotube_logged_out') === 'true') {
      return false;
    }
    return !!getActiveAccount();
  });

  const [exploreDismissed, setExploreDismissed] = useState(() => {
    return localStorage.getItem('speedotube_explore_dismissed') === 'true';
  });
  const [isDismissing, setIsDismissing] = useState(false);

  // Global Bottom-Left Toast Notification State
  const [globalToast, setGlobalToast] = useState({
    isOpen: false,
    message: '',
    icon: null,
    toastKey: null,
  });

  const showGlobalToast = useCallback((message, icon = null) => {
    setGlobalToast({
      isOpen: true,
      message,
      icon,
      toastKey: Date.now(),
    });
  }, []);

  useEffect(() => {
    const handleToastEvent = (e) => {
      const msg = e.detail?.message;
      const icon = e.detail?.icon || null;
      if (msg) {
        showGlobalToast(msg, icon);
      }
    };
    window.addEventListener('speedotube_show_toast', handleToastEvent);
    return () => window.removeEventListener('speedotube_show_toast', handleToastEvent);
  }, [showGlobalToast]);

  // Global Persistent Music Audio Session State
  const globalAudioRef = useRef(null);
  const [musicSession, setMusicSession] = useState({
    album: null,
    currentTrackIndex: 0,
    isPlaying: false,
    currentTime: 0,
    duration: 243,
    volume: 1,
    isMuted: false,
    isShuffle: false,
    repeatMode: 'off', // 'off' | 'all' | 'one'
    isMiniplayerDismissed: false,
  });

  const selectMusicTrack = useCallback((index, startPlaying = true) => {
    setMusicSession((prev) => {
      const tracks = prev.album?.tracks;
      if (!tracks || !tracks[index]) return prev;
      const track = tracks[index];
      const trackUrl = normalizeMediaUrl(track.audioUrl || track.url);
      if (globalAudioRef.current) {
        if (trackUrl && globalAudioRef.current.src !== trackUrl) {
          globalAudioRef.current.src = trackUrl;
        }
        globalAudioRef.current.currentTime = 0;
        if (startPlaying && trackUrl) {
          globalAudioRef.current.play().catch((err) => console.warn('Track play error:', err));
        }
      }
      return {
        ...prev,
        currentTrackIndex: index,
        isPlaying: startPlaying,
        currentTime: 0,
        duration: track.durationSec || prev.duration,
        isMiniplayerDismissed: false,
      };
    });
  }, []);

  const toggleMusicPlay = useCallback(() => {
    if (!globalAudioRef.current) return;
    if (musicSession.isPlaying) {
      globalAudioRef.current.pause();
      setMusicSession((prev) => ({ ...prev, isPlaying: false }));
    } else {
      const activeTrack = musicSession.album?.tracks?.[musicSession.currentTrackIndex] || musicSession.album?.tracks?.[0];
      const targetSrc = normalizeMediaUrl(activeTrack?.audioUrl || activeTrack?.url);
      if (targetSrc) {
        try {
          const fullTargetUrl = new URL(targetSrc, window.location.origin).href;
          if (!globalAudioRef.current.src || globalAudioRef.current.src !== fullTargetUrl) {
            globalAudioRef.current.src = targetSrc;
          }
        } catch (e) {
          globalAudioRef.current.src = targetSrc;
        }
        globalAudioRef.current.play().then(() => {
          setMusicSession((prev) => ({ ...prev, isPlaying: true, isMiniplayerDismissed: false }));
        }).catch((err) => {
          console.warn('Music play failed:', err);
          if (globalAudioRef.current.src !== targetSrc) {
            globalAudioRef.current.src = targetSrc;
            globalAudioRef.current.play().catch(() => {});
          }
        });
      }
    }
  }, [musicSession.isPlaying, musicSession.album, musicSession.currentTrackIndex]);

  const pauseMusic = useCallback(() => {
    if (globalAudioRef.current && !globalAudioRef.current.paused) {
      globalAudioRef.current.pause();
    }
    setMusicSession((prev) => ({ ...prev, isPlaying: false }));
  }, []);

  const seekMusic = useCallback((time) => {
    if (globalAudioRef.current) {
      globalAudioRef.current.currentTime = time;
    }
    setMusicSession((prev) => ({ ...prev, currentTime: time }));
  }, []);

  const nextMusicTrack = useCallback(() => {
    const tracks = musicSession.album?.tracks;
    if (!tracks || tracks.length === 0) return;
    let nextIdx;
    if (musicSession.isShuffle) {
      nextIdx = Math.floor(Math.random() * tracks.length);
    } else {
      nextIdx = (musicSession.currentTrackIndex + 1) % tracks.length;
    }
    selectMusicTrack(nextIdx, true);
  }, [musicSession.album, musicSession.isShuffle, musicSession.currentTrackIndex, selectMusicTrack]);

  const prevMusicTrack = useCallback(() => {
    if (globalAudioRef.current && globalAudioRef.current.currentTime > 3) {
      globalAudioRef.current.currentTime = 0;
      setMusicSession((prev) => ({ ...prev, currentTime: 0 }));
      return;
    }
    const tracks = musicSession.album?.tracks;
    if (!tracks || tracks.length === 0) return;
    const prevIdx = (musicSession.currentTrackIndex - 1 + tracks.length) % tracks.length;
    selectMusicTrack(prevIdx, true);
  }, [musicSession.album, musicSession.currentTrackIndex, selectMusicTrack]);

  const toggleMusicShuffle = useCallback(() => {
    setMusicSession((prev) => ({ ...prev, isShuffle: !prev.isShuffle }));
  }, []);

  const toggleMusicRepeat = useCallback(() => {
    setMusicSession((prev) => {
      const modes = ['off', 'all', 'one'];
      const nextIdx = (modes.indexOf(prev.repeatMode) + 1) % modes.length;
      return { ...prev, repeatMode: modes[nextIdx] };
    });
  }, []);

  const setMusicVolume = useCallback((vol) => {
    const clamped = Math.max(0, Math.min(1, vol));
    if (globalAudioRef.current) {
      globalAudioRef.current.volume = clamped;
      globalAudioRef.current.muted = clamped === 0;
    }
    setMusicSession((prev) => ({ ...prev, volume: clamped, isMuted: clamped === 0 }));
  }, []);

  const toggleMusicMute = useCallback(() => {
    setMusicSession((prev) => {
      const nextMuted = !prev.isMuted;
      if (globalAudioRef.current) {
        globalAudioRef.current.muted = nextMuted;
      }
      return { ...prev, isMuted: nextMuted };
    });
  }, []);

  const closeMiniplayer = useCallback(() => {
    if (globalAudioRef.current) {
      try {
        globalAudioRef.current.pause();
        globalAudioRef.current.currentTime = 0;
        globalAudioRef.current.src = '';
      } catch (e) {}
    }
    setMusicSession({
      album: null,
      currentTrackIndex: 0,
      isPlaying: false,
      currentTime: 0,
      duration: 243,
      volume: 1,
      isMuted: false,
      isShuffle: false,
      repeatMode: 'off',
      isMiniplayerDismissed: true,
    });
  }, []);

  const loadAlbumToSession = useCallback((albumData, autoPlay = false) => {
    if (!albumData) return;
    setMusicSession((prev) => {
      const firstTrack = albumData.tracks?.[0];
      const audioUrl = normalizeMediaUrl(firstTrack?.audioUrl || firstTrack?.url);

      if (prev.album?.id === albumData.id) {
        if (globalAudioRef.current && audioUrl) {
          try {
            const fullUrl = new URL(audioUrl, window.location.origin).href;
            if (!globalAudioRef.current.src || globalAudioRef.current.src !== fullUrl) {
              globalAudioRef.current.src = audioUrl;
            }
          } catch (e) {
            globalAudioRef.current.src = audioUrl;
          }
        }
        if (autoPlay && !prev.isPlaying && globalAudioRef.current) {
          globalAudioRef.current.play().catch(() => {});
          return { ...prev, isPlaying: true, isMiniplayerDismissed: false };
        }
        return prev;
      }

      if (globalAudioRef.current && audioUrl) {
        try {
          const fullUrl = new URL(audioUrl, window.location.origin).href;
          if (!globalAudioRef.current.src || globalAudioRef.current.src !== fullUrl) {
            globalAudioRef.current.src = audioUrl;
          }
        } catch (e) {
          globalAudioRef.current.src = audioUrl;
        }
        globalAudioRef.current.currentTime = 0;
        if (autoPlay) {
          globalAudioRef.current.play().catch((err) => console.warn('AutoPlay error:', err));
        }
      }
      return {
        ...prev,
        album: albumData,
        currentTrackIndex: 0,
        isPlaying: autoPlay,
        currentTime: 0,
        duration: firstTrack?.durationSec || 243,
        isMiniplayerDismissed: false,
      };
    });
    musicWatchTimeRef.current = 0;
    musicLastTimestampRef.current = null;
  }, []);

  const handleAudioEnded = useCallback(() => {
    setMusicSession((prev) => {
      if (prev.repeatMode === 'one') {
        if (globalAudioRef.current) {
          globalAudioRef.current.currentTime = 0;
          globalAudioRef.current.play().catch(() => {});
        }
        return prev;
      }
      const tracks = prev.album?.tracks;
      if (!tracks || tracks.length === 0) return { ...prev, isPlaying: false };
      if (prev.repeatMode === 'all' || prev.currentTrackIndex < tracks.length - 1) {
        let nextIdx = (prev.currentTrackIndex + 1) % tracks.length;
        if (prev.isShuffle) {
          nextIdx = Math.floor(Math.random() * tracks.length);
        }
        const nextTrack = tracks[nextIdx];
        const nextUrl = normalizeMediaUrl(nextTrack?.audioUrl || nextTrack?.url);
        if (globalAudioRef.current && nextUrl) {
          globalAudioRef.current.src = nextUrl;
          globalAudioRef.current.currentTime = 0;
          globalAudioRef.current.play().catch(() => {});
        }
        return {
          ...prev,
          currentTrackIndex: nextIdx,
          isPlaying: true,
          currentTime: 0,
          duration: nextTrack?.durationSec || 243,
        };
      }
      return { ...prev, isPlaying: false };
    });
  }, []);

  const musicWatchTimeRef = useRef(0);
  const musicLastTimestampRef = useRef(null);
  const musicViewRegisteredRef = useRef({});

  const handleAudioTimeUpdate = useCallback(() => {
    if (!globalAudioRef.current) return;
    const curTime = globalAudioRef.current.currentTime;
    setMusicSession((prev) => ({
      ...prev,
      currentTime: curTime,
    }));

    // Video-style Threshold of Adding Views (30s continuous forward listening)
    if (!globalAudioRef.current.paused && musicSession.album?.id) {
      const albId = musicSession.album.id;
      const now = performance.now();
      if (musicLastTimestampRef.current === null) {
        musicLastTimestampRef.current = now;
        return;
      }
      const deltaSec = (now - musicLastTimestampRef.current) / 1000;
      musicLastTimestampRef.current = now;

      if (deltaSec > 0 && deltaSec < 2.0) {
        musicWatchTimeRef.current += deltaSec;
      }

      const targetDuration = globalAudioRef.current.duration || musicSession.duration || 243;
      const listenThreshold = Math.min(30, targetDuration * 0.5);

      if (musicWatchTimeRef.current >= listenThreshold && !musicViewRegisteredRef.current[albId]) {
        musicViewRegisteredRef.current[albId] = true;
        fetch(`${API_BASE_URL}/api/v1/albums/${albId}/view`, {
          method: 'POST',
          headers: { devicetype: '0', 'Content-Type': 'application/json' },
        })
          .then((res) => res.json())
          .then((json) => {
            if (json?.data?.view_count !== undefined) {
              setMusicSession((prev) => ({
                ...prev,
                album: prev.album ? { ...prev.album, plays: json.data.view_count, views: json.data.view_count } : prev.album,
              }));
              window.dispatchEvent(new CustomEvent('speedotube_album_view_counted', {
                detail: { albumId: albId, viewCount: json.data.view_count }
              }));
            }
          })
          .catch((err) => console.warn('Album view register error:', err));
      }
    }
  }, [musicSession.album, musicSession.duration]);

  // Auto-Detect CRM Redirect & Save User Account on Load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authSuccess = params.get('speedonet_auth') === 'success' || params.get('speedonet_login') === '1';
    const crmName = params.get('name') || params.get('customer_name') || params.get('user');
    const crmEmail = params.get('email') || '';
    const crmUsername = params.get('username') || params.get('user_id');

    if (authSuccess || crmName || crmEmail) {
      const name = crmName || 'ANJU TEST';
      const email = crmEmail || 'kad.anju2@gmail.com';
      const username = crmUsername || name.toLowerCase().replace(/[^a-z0-9]/gi, '');
      const handle = `@${username}`;
      const initial = name.charAt(0).toUpperCase();

      const avatarBg = getAvatarColor(name || email);

      const newAccount = {
        name,
        email,
        username,
        handle,
        initial,
        avatarBg,
        provider: 'Speedonet Broadband',
        hasChannel: true,
        rememberMe: true,
        createdAt: new Date().toISOString(),
        loginTime: new Date().toISOString(),
      };

      localStorage.setItem('speedotube_user_account', JSON.stringify(newAccount));
      setUserProfile(newAccount);
      setIsLoggedIn(true);

      // Clean URL parameters smoothly without page reload
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);


  // Hydrate live channel customization from backend on mount so user changes persist across page refresh
  useEffect(() => {
    let isMounted = true;
    const syncProfileFromBackend = async () => {
      const active = getActiveAccount();
      if (!active || localStorage.getItem('speedotube_logged_out') === 'true') {
        return;
      }
      try {
        const customData = await fetchChannelCustomization(active.channelId || active.id, active.handle);
        if (customData && isMounted) {
          const newName = customData.name || active.name;
          const updated = {
            ...active,
            name: newName,
            fullName: newName,
            initial: (newName && newName.trim()) ? newName.trim().charAt(0).toUpperCase() : 'U',
            avatarBg: getAvatarColor(newName || customData.handle_name || active.handle),
            handle: customData.handle_name || active.handle,
            email: customData.email || active.email,
            avatar_url: customData.avatar_url || active.avatar_url,
            profile_image: customData.avatar_url || active.profile_image,
            banner_url: customData.banner_url || active.banner_url,
          };
          localStorage.setItem('speedotube_user_account', JSON.stringify(updated));
          setUserProfile(updated);
        }
      } catch (err) {
        console.warn('Could not sync user profile from backend on mount:', err);
      }
    };
    syncProfileFromBackend();
    return () => {
      isMounted = false;
    };
  }, [isLoggedIn]);

  const closeWatchDrawer = useCallback(() => {
    if (isDrawerClosing || !watchDrawerOpen) return;
    setIsDrawerClosing(true);
    setTimeout(() => {
      setWatchDrawerOpen(false);
      setIsDrawerClosing(false);
    }, 240);
  }, [isDrawerClosing, watchDrawerOpen]);

  const closeMobileDrawer = useCallback(() => {
    if (isMobileDrawerClosing || !mobileDrawerOpen) return;
    setIsMobileDrawerClosing(true);
    setTimeout(() => {
      setMobileDrawerOpen(false);
      setIsMobileDrawerClosing(false);
    }, 240);
  }, [isMobileDrawerClosing, mobileDrawerOpen]);

  // Close drawer on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (watchDrawerOpen && !isDrawerClosing) {
          closeWatchDrawer();
        }
        if (mobileDrawerOpen && !isMobileDrawerClosing) {
          closeMobileDrawer();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [watchDrawerOpen, isDrawerClosing, closeWatchDrawer, mobileDrawerOpen, isMobileDrawerClosing, closeMobileDrawer]);

  const toggleSidebar = () => {
    if (isWatchPage(activePath)) {
      if (watchDrawerOpen && !isDrawerClosing) {
        closeWatchDrawer();
      } else if (!watchDrawerOpen && !isDrawerClosing) {
        setIsDrawerClosing(false);
        setWatchDrawerOpen(true);
      }
    } else if (isMobileOrTablet) {
      if (mobileDrawerOpen && !isMobileDrawerClosing) {
        closeMobileDrawer();
      } else if (!mobileDrawerOpen && !isMobileDrawerClosing) {
        setIsMobileDrawerClosing(false);
        setMobileDrawerOpen(true);
      }
    } else {
      setSidebarCollapsed((prev) => !prev);
    }
  };

  const handleNavigate = (path, skipPush = false) => {
    if (isWatchPage(path)) {
      const isAlbum = path.includes('album=');
      // If user selected a video (not an album), automatically close the album miniplayer
      if (!isAlbum) {
        closeMiniplayer();
      }

      const match = path.match(/[?&]v=([^&]+)/);
      const vid = match ? match[1] : null;
      if (vid) setWatchVideoId(vid);
      localStorage.setItem('speedotube_active_path', path);
      setActivePath(path);
      setIsUploadModalOpen(false);
      closeWatchDrawer();

      if (!skipPush) {
        window.history.pushState({ path, videoId: vid }, '', path);
      }
      return;
    }

    if (path === '/studio/upload-video' || path === '/studio/upload') {
      localStorage.setItem('speedotube_active_path', '/studio');
      setActivePath('/studio');
      setUploadModalVideoType('video');
      setIsUploadModalOpen(true);

      if (!skipPush) {
        window.history.pushState({ path: '/studio', modal: 'upload-video' }, '', '/studio');
      }
      return;
    }

    if (path === '/studio/upload-short' || path === '/shorts/upload') {
      localStorage.setItem('speedotube_active_path', '/studio');
      setActivePath('/studio');
      setUploadModalVideoType('short');
      setIsUploadModalOpen(true);

      if (!skipPush) {
        window.history.pushState({ path: '/studio', modal: 'upload-short' }, '', '/studio');
      }
      return;
    }

    if (path === '/studio/upload-album' || path === '/albums/upload') {
      localStorage.setItem('speedotube_active_path', '/studio');
      setActivePath('/studio');
      setUploadModalVideoType('album');
      setIsUploadModalOpen(true);

      if (!skipPush) {
        window.history.pushState({ path: '/studio', modal: 'upload-album' }, '', '/studio');
      }
      return;
    }

    localStorage.setItem('speedotube_active_path', path);
    setActivePath(path);
    setIsUploadModalOpen(false);
    closeWatchDrawer();
    closeMobileDrawer();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (!skipPush) {
      window.history.pushState({ path }, '', path);
    }
  };

  // Listen for global custom speedotube_navigate event
  useEffect(() => {
    const handleCustomNav = (e) => {
      const targetPath = e.detail?.path;
      if (targetPath) {
        handleNavigate(targetPath);
      }
    };
    window.addEventListener('speedotube_navigate', handleCustomNav);
    return () => window.removeEventListener('speedotube_navigate', handleCustomNav);
  }, []);

  // Synchronize document.title dynamically with active music session or page route
  useEffect(() => {
    // 1. If currently playing an album/music track in background or miniplayer
    if (musicSession?.isPlaying && musicSession?.album) {
      const activeTrack = musicSession.album.tracks?.[musicSession.currentTrackIndex];
      const trackTitle = activeTrack?.title || musicSession.album.title || 'Music';
      const artist = activeTrack?.artist || musicSession.album.artist || musicSession.album.creator || '';
      document.title = `${trackTitle}${artist ? ` - ${artist}` : ''} - SpeedoTube`;
      return;
    }

    // 2. Otherwise update based on route if not on dynamic pages that set their own title
    if (activePath === '/' || activePath === '') {
      document.title = 'SpeedoTube';
    } else if (activePath === '/shorts' || activePath.startsWith('/shorts')) {
      // ShortsPage handles active short title, fallback:
      if (!document.title.includes('- SpeedoTube')) {
        document.title = 'Shorts - SpeedoTube';
      }
    } else if (activePath === '/albums') {
      document.title = 'Speedo Music & Albums - SpeedoTube';
    } else if (activePath.startsWith('/studio')) {
      document.title = 'SpeedoTube Studio';
    } else if (activePath === '/history') {
      document.title = 'History - SpeedoTube';
    } else if (activePath === '/watch-later') {
      document.title = 'Watch Later - SpeedoTube';
    } else if (activePath === '/liked-videos') {
      document.title = 'Liked Videos - SpeedoTube';
    } else if (activePath === '/playlists') {
      document.title = 'Playlists - SpeedoTube';
    } else if (activePath === '/your-videos') {
      document.title = 'Your Videos - SpeedoTube';
    } else if (activePath === '/downloads') {
      document.title = 'Downloads - SpeedoTube';
    }
  }, [musicSession?.isPlaying, musicSession?.album, musicSession?.currentTrackIndex, activePath]);

  // Browser Back Arrow (popstate) Handler to prevent exiting to Google
  useEffect(() => {
    if (!window.history.state) {
      const fullUrl = window.location.pathname + window.location.search;
      window.history.replaceState({ path: activePath }, '', fullUrl);
    }

    const handlePopState = (event) => {
      const state = event.state;

      // Close upload modal if open
      if (isUploadModalOpen) {
        setIsUploadModalOpen(false);
        return;
      }

      const currentSearch = window.location.search;
      const currentPath = window.location.pathname;
      const targetPath =
        state?.path ||
        (isWatchPage(currentPath) ? '/watch' + currentSearch : currentPath || '/');
      const params = new URLSearchParams(currentSearch);
      const vid = params.get('v') || state?.videoId || null;
      if (vid) setWatchVideoId(vid);

      if (isWatchPage(targetPath) && !targetPath.includes('album=')) {
        closeMiniplayer();
      }

      localStorage.setItem('speedotube_active_path', targetPath);
      setActivePath(targetPath);
      setIsUploadModalOpen(false);
      setWatchDrawerOpen(false);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isUploadModalOpen, activePath]);

  // Synchronize active account across the entire app on switch or channel customization update
  useEffect(() => {
    const handleAccountSwitched = (e) => {
      closeMiniplayer();
      const switchedAccount = e.detail;
      if (switchedAccount) {
        localStorage.removeItem('speedotube_logged_out');
        setUserProfile(switchedAccount);
        setIsLoggedIn(true);
      }
    };
    const handleResetMusic = () => {
      closeMiniplayer();
    };
    const handleChannelUpdated = (e) => {
      const updatedChannel = e.detail;
      if (updatedChannel) {
        setUserProfile((prev) => {
          const acc = { ...(prev || {}) };
          if (updatedChannel.name) {
            acc.name = updatedChannel.name;
            acc.fullName = updatedChannel.name;
            acc.initial = updatedChannel.name.trim().charAt(0).toUpperCase();
            acc.avatarBg = getAvatarColor(updatedChannel.name);
          }
          if (updatedChannel.handle_name) acc.handle = updatedChannel.handle_name;
          if (updatedChannel.avatar_url !== undefined) {
            acc.avatar_url = updatedChannel.avatar_url;
            acc.profile_image = updatedChannel.avatar_url;
          }
          if (updatedChannel.banner_url !== undefined) acc.banner_url = updatedChannel.banner_url;
          return acc;
        });
      }
    };
    window.addEventListener('speedotube_reset_music_session', handleResetMusic);
    window.addEventListener('speedotube_user_switched', handleAccountSwitched);
    window.addEventListener('speedotube_channel_updated', handleChannelUpdated);
    return () => {
      window.removeEventListener('speedotube_reset_music_session', handleResetMusic);
      window.removeEventListener('speedotube_user_switched', handleAccountSwitched);
      window.removeEventListener('speedotube_channel_updated', handleChannelUpdated);
    };
  }, [closeMiniplayer]);

  const handleAuthChange = (status) => {
    if (!status) {
      closeMiniplayer();
      localStorage.removeItem('speedotube_user_account');
      sessionStorage.removeItem('speedotube_user_account');
      localStorage.removeItem('speedotube_user_token');
      localStorage.removeItem('speedotube_explore_dismissed');
      localStorage.setItem('speedotube_logged_out', 'true');
      localStorage.setItem('speedotube_active_path', '/');
      setUserProfile(null);
      setIsLoggedIn(false);
      setExploreDismissed(false);
      setIsDismissing(false);
      setActivePath('/');
      window.history.pushState({}, '', '/');
      window.scrollTo({ top: 0, behavior: 'instant' });
    } else if (typeof status === 'object') {
      closeMiniplayer();
      localStorage.removeItem('speedotube_logged_out');
      setUserProfile(status);
      setIsLoggedIn(true);
    } else {
      handleGoToAuth();
    }
  };

  const handleGoToAuth = () => {
    localStorage.setItem('speedotube_active_path', '/auth');
    setActivePath('/auth');
    window.history.pushState({}, '', '/auth');
  };

  const handleLoginSuccess = (userData) => {
    closeMiniplayer();
    localStorage.removeItem('speedotube_logged_out');
    localStorage.setItem('speedotube_explicit_login', 'true');
    const name = userData?.name || 'Speedo User';
    const email = userData?.email || '';
    const username = userData?.username || name.toLowerCase().replace(/[^a-z0-9]/gi, '');
    const handle = userData?.handle || `@${username}`;
    const initial = name.charAt(0).toUpperCase();
    const avatarBg = userData?.avatarBg || getAvatarColor(name || email);
    const shouldRemember = userData?.rememberMe !== false;

    const baseAccount = {
      name,
      email,
      username,
      handle,
      initial,
      avatarBg,
      provider: userData?.provider || 'Email',
      hasChannel: true,
      rememberMe: shouldRemember,
      createdAt: userData?.createdAt || new Date().toISOString(),
      loginTime: new Date().toISOString(),
      ...(userData || {}),
    };

    const account = hydrateAccount(baseAccount);

    if (shouldRemember) {
      localStorage.setItem('speedotube_user_account', JSON.stringify(account));
      sessionStorage.removeItem('speedotube_user_account');
    } else {
      sessionStorage.setItem('speedotube_user_account', JSON.stringify(account));
      localStorage.removeItem('speedotube_user_account');
    }

    if (account.token) {
      localStorage.setItem('speedotube_user_token', account.token);
    }

    localStorage.setItem('speedotube_active_path', '/');
    setUserProfile(account);
    setIsLoggedIn(true);
    setExploreDismissed(false);
    setIsDismissing(false);
    window.dispatchEvent(new CustomEvent('speedotube_user_switched', { detail: account }));
    setActivePath('/');
    window.history.pushState({}, '', '/');

    // Asynchronously fetch and apply latest live channel customization from MySQL backend
    fetchChannelCustomization(account.channelId || account.id, account.handle)
      .then((customData) => {
        if (customData) {
          const updated = {
            ...account,
            name: newName,
            fullName: newName,
            initial: (newName && newName.trim()) ? newName.trim().charAt(0).toUpperCase() : 'U',
            avatarBg: getAvatarColor(newName || customData.handle_name || account.handle),
            handle: customData.handle_name || account.handle,
            email: customData.email || account.email,
            avatar_url: customData.avatar_url || account.avatar_url,
            profile_image: customData.avatar_url || account.profile_image,
            banner_url: customData.banner_url || account.banner_url,
          };
          if (shouldRemember) {
            localStorage.setItem('speedotube_user_account', JSON.stringify(updated));
          } else {
            sessionStorage.setItem('speedotube_user_account', JSON.stringify(updated));
          }
          setUserProfile(updated);
          window.dispatchEvent(new CustomEvent('speedotube_user_switched', { detail: updated }));
        }
      })
      .catch((e) => console.warn('Could not sync user profile on login:', e));
  };

  const handleExploreAnyways = () => {
    setIsDismissing(true);
    setTimeout(() => {
      localStorage.setItem('speedotube_explore_dismissed', 'true');
      setExploreDismissed(true);
      setIsDismissing(false);
    }, 350);
  };

  const [, setLangTick] = useState(0);
  useEffect(() => {
    const handleLangChange = () => setLangTick((t) => t + 1);
    window.addEventListener('speedotube_language_changed', handleLangChange);
    return () => window.removeEventListener('speedotube_language_changed', handleLangChange);
  }, []);

  const showBlurOverlay = (!isLoggedIn && !exploreDismissed && activePath !== '/auth') || isDismissing;

  if (activePath === '/auth') {
    return (
      <AuthPage
        onLoginSuccess={handleLoginSuccess}
        onBackToHome={() => {
          localStorage.setItem('speedotube_active_path', '/');
          window.history.pushState({}, '', '/');
          setActivePath('/');
        }}
      />
    );
  }

  return (
    <div className="app-container">
      {/* Frosted Glass Header */}
      <Header
        onToggleSidebar={toggleSidebar}
        isLoggedIn={isLoggedIn}
        userProfile={userProfile}
        onAuthChange={handleAuthChange}
        onNavigate={handleNavigate}
        activePath={activePath}
      />

      {/* Main Layout: Sidebar + Content */}
      <div className="main-layout" style={{ marginTop: '56px', display: 'flex', flex: 1 }}>
        {activePath.startsWith('/studio') ? (
          <StudioPage
            userProfile={userProfile}
            activeTab={
              activePath.includes('speedoearn') || activePath.includes('earn')
                ? 'speedoearn'
                : activePath.includes('content')
                ? 'content'
                : activePath.includes('albums')
                ? 'albums'
                : activePath.includes('analytics')
                ? 'analytics'
                : activePath.includes('comments')
                ? 'comments'
                : activePath.includes('customisation') || activePath.includes('customization')
                ? 'customization'
                : activePath.includes('audio')
                ? 'audio'
                : 'dashboard'
            }
            onNavigate={handleNavigate}
          />
        ) : (
          <>
            {!isWatchPage(activePath) && (
              <Sidebar
                collapsed={sidebarCollapsed}
                activePath={activePath}
                onNavigate={handleNavigate}
                isLoggedIn={isLoggedIn}
                onLogin={handleGoToAuth}
              />
            )}

            {/* Watch Page Floating Sidebar Drawer when hamburger is opened */}
            {isWatchPage(activePath) && (watchDrawerOpen || isDrawerClosing) && (
              <div
                className={`yt-watch-sidebar-drawer-backdrop ${isDrawerClosing ? 'closing' : 'opening'}`}
                onClick={closeWatchDrawer}
              >
                <div
                  className={`yt-watch-sidebar-drawer ${isDrawerClosing ? 'closing' : 'opening'}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Sidebar
                    collapsed={false}
                    activePath={activePath}
                    onNavigate={(p) => {
                      closeWatchDrawer();
                      handleNavigate(p);
                    }}
                    isLoggedIn={isLoggedIn}
                    onLogin={handleGoToAuth}
                  />
                </div>
              </div>
            )}

            {/* Mobile / Tablet Floating Sidebar Drawer when hamburger is opened on non-watch pages */}
            {!isWatchPage(activePath) && (mobileDrawerOpen || isMobileDrawerClosing) && (
              <div
                className={`yt-watch-sidebar-drawer-backdrop ${isMobileDrawerClosing ? 'closing' : 'opening'}`}
                onClick={closeMobileDrawer}
              >
                <div
                  className={`yt-watch-sidebar-drawer ${isMobileDrawerClosing ? 'closing' : 'opening'}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Sidebar
                    collapsed={false}
                    activePath={activePath}
                    onNavigate={(p) => {
                      closeMobileDrawer();
                      handleNavigate(p);
                    }}
                    isLoggedIn={isLoggedIn}
                    onLogin={handleGoToAuth}
                  />
                </div>
              </div>
            )}

            <main
              className={`content-area ${!isWatchPage(activePath) ? (sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded') : ''} ${showBlurOverlay ? 'content-area-blurred' : ''} ${isDismissing ? 'exiting' : ''}`}
              style={
                activePath === '/shorts' || activePath.startsWith('/shorts')
                  ? { padding: 0 }
                  : isWatchPage(activePath)
                  ? { padding: '24px 0 0 0', maxWidth: '100%' }
                  : {}
              }
            >
              <div
                key={
                  activePath.startsWith('/shorts')
                    ? 'page-shorts'
                    : isWatchPage(activePath)
                    ? 'page-watch'
                    : activePath.split('?')[0]
                }
                className="page-transition-wrapper"
              >
                {isWatchPage(activePath) ? (
                  <WatchPage
                    videoUrlId={watchVideoId}
                    activePath={activePath}
                    userProfile={userProfile}
                    isLoggedIn={isLoggedIn}
                    onLogin={handleGoToAuth}
                    onNavigate={handleNavigate}
                    musicSession={musicSession}
                    onToggleMusicPlay={toggleMusicPlay}
                    onSelectMusicTrack={selectMusicTrack}
                    onSeekMusic={seekMusic}
                    onLoadAlbum={loadAlbumToSession}
                    onPauseMusic={pauseMusic}
                    onCloseMiniplayer={closeMiniplayer}
                  />
                ) : activePath === '/shorts' || activePath.startsWith('/shorts') ? (
                  <ShortsPage
                    userProfile={userProfile}
                    isLoggedIn={isLoggedIn}
                    onLogin={handleGoToAuth}
                    initialShortId={
                      activePath.startsWith('/shorts/')
                        ? activePath.replace('/shorts/', '')
                        : (new URLSearchParams(window.location.search).get('v') || null)
                    }
                    onNavigate={handleNavigate}
                  />
                ) : activePath === '/albums' ? (
                  <AlbumsPage
                    userProfile={userProfile}
                    isLoggedIn={isLoggedIn}
                    onLogin={handleGoToAuth}
                    onNavigate={handleNavigate}
                  />
                ) : activePath === '/profile' || activePath.startsWith('/channel/') ? (
                  <ChannelPage
                    userProfile={userProfile}
                    isLoggedIn={isLoggedIn}
                    onLogin={handleGoToAuth}
                    activePath={activePath}
                    channelHandle={activePath.startsWith('/channel/') ? decodeURIComponent(activePath.replace('/channel/', '')) : null}
                    onNavigate={handleNavigate}
                    onOpenUpload={() => {
                      setUploadModalVideoType('video');
                      setIsUploadModalOpen(true);
                    }}
                  />
                ) : activePath === '/history' ? (
                  <HistoryPage userProfile={userProfile} onNavigate={handleNavigate} />
                ) : activePath === '/watch-later' ? (
                  <WatchLaterPage userProfile={userProfile} onNavigate={handleNavigate} />
                ) : activePath === '/liked-videos' ? (
                  <LikedVideosPage userProfile={userProfile} onNavigate={handleNavigate} />
                ) : activePath === '/playlists' ? (
                  <PlaylistsPage userProfile={userProfile} onNavigate={handleNavigate} />
                ) : activePath === '/your-videos' ? (
                  <YourVideosPage
                    userProfile={userProfile}
                    onNavigate={handleNavigate}
                    onOpenUpload={() => {
                      setUploadModalVideoType('video');
                      setIsUploadModalOpen(true);
                    }}
                  />
                ) : activePath === '/downloads' ? (
                  <DownloadsPage userProfile={userProfile} onNavigate={handleNavigate} />
                ) : activePath.startsWith('/results') ? (
                  <SearchResultsPage
                    key={
                      new URLSearchParams(
                        (activePath.includes('?') ? activePath.substring(activePath.indexOf('?')) : '') ||
                        window.location.search ||
                        ''
                      ).get('search_query') || 'empty-search'
                    }
                    searchQuery={
                      new URLSearchParams(
                        (activePath.includes('?') ? activePath.substring(activePath.indexOf('?')) : '') ||
                        window.location.search ||
                        ''
                      ).get('search_query') || ''
                    }
                    onNavigate={handleNavigate}
                  />
                ) : (
                  <VideoGrid selectedCategory={selectedCategory} onNavigate={handleNavigate} />
                )}
              </div>

              {/* Logged Out Content Area Blur Overlay */}
              {showBlurOverlay && (
                <div className={`yt-content-blur-overlay ${isDismissing ? 'exiting' : ''}`}>
                  <div className="yt-blur-modal-card">
                    <div className="yt-blur-modal-icon">
                      <HiOutlineUserCircle size={58} />
                    </div>
                    <h2 className="yt-blur-modal-title">{getTranslation('welcomeTitle')}</h2>
                    <p className="yt-blur-modal-message">
                      {getTranslation('welcomeMessage')}
                    </p>
                    <div className="yt-blur-modal-actions">
                      <button className="yt-blur-signin-btn" onClick={handleGoToAuth}>
                        {getTranslation('signIn')}
                      </button>
                      <button className="yt-blur-explore-btn" onClick={handleExploreAnyways}>
                        {getTranslation('exploreAnyways')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* SpeedoTube INC Official Global Footer (Properly implemented across every section of the webpage, excluded from Watch & Shorts) */}
              {!isWatchPage(activePath) && !activePath.startsWith('/shorts') && (
                <Footer onNavigate={handleNavigate} />
              )}
            </main>
          </>
        )}
      </div>

      {/* Production-Ready SpeedoTube Creator Upload Modal */}
      <SpeedoTubeUploadModal
        isOpen={isUploadModalOpen}
        initialVideoType={uploadModalVideoType}
        onClose={() => setIsUploadModalOpen(false)}
        onPublishSuccess={async (data) => {
          showGlobalToast(
            'Your Content has been Send to Approval to Monitor Team ..',
            <MdInfoOutline size={20} style={{ color: '#3ea6ff' }} />
          );
          if (data && data.isAlbum) {
            window.dispatchEvent(new Event('speedotube_album_published'));
          } else if (data) {
            const activeUser = getActiveAccount() || {};
            const chId = userProfile?.channelId || activeUser.channelId || null;
            const uId = userProfile?.userId || userProfile?.id || activeUser.userId || activeUser.id || null;
            const chHandle = userProfile?.handle || activeUser.handle || (userProfile?.name ? `@${userProfile.name.toLowerCase().replace(/[^a-z0-9]/gi, '')}` : null);
            await uploadChannelVideo({
              title: data.title,
              description: data.description,
              tags: data.tags,
              category: data.category,
              license: data.license,
              visibility: data.visibility,
              duration: data.fileMetadata?.duration || '02:30',
              thumbnailUrl: data.thumbnailUrl,
              channelId: chId,
              userId: uId,
              channelHandle: chHandle,
              isShort: data.type === 'short',
              file: data.file,
            }, chId, chHandle);
            window.dispatchEvent(new Event('speedotube_video_published'));
          }
        }}
      />

      {/* Global SpeedoTube Music Audio Element (Persistent Across All Page / Tab Switches) */}
      <audio
        ref={globalAudioRef}
        preload="auto"
        onTimeUpdate={handleAudioTimeUpdate}
        onLoadedMetadata={() => {
          if (globalAudioRef.current && globalAudioRef.current.duration) {
            setMusicSession((prev) => ({
              ...prev,
              duration: globalAudioRef.current.duration,
            }));
          }
        }}
        onPlay={() => {
          setMusicSession((prev) => ({ ...prev, isPlaying: true }));
        }}
        onPause={() => {
          setMusicSession((prev) => ({ ...prev, isPlaying: false }));
        }}
        onEnded={handleAudioEnded}
      />

      {/* Floating Spotify-Style Music MiniPlayer (Appears smoothly at right bottom side when user leaves watch page while playing music) */}
      {musicSession.album && (
        <MusicMiniPlayer
          isOpen={!isWatchPage(activePath) && !musicSession.isMiniplayerDismissed}
          musicSession={musicSession}
          onTogglePlay={toggleMusicPlay}
          onSeek={seekMusic}
          onNext={nextMusicTrack}
          onPrev={prevMusicTrack}
          onToggleShuffle={toggleMusicShuffle}
          onToggleRepeat={toggleMusicRepeat}
          onSetVolume={setMusicVolume}
          onToggleMute={toggleMusicMute}
          onClose={closeMiniplayer}
          onNavigate={handleNavigate}
        />
      )}

      {/* Mobile Bottom Navigation Bar (YouTube Mobile Experience) */}
      {isMobileOrTablet && !isWatchPage(activePath) && !activePath.startsWith('/shorts') && (
        <nav className="yt-mobile-bottom-nav">
          <button
            type="button"
            className={`yt-mobile-bottom-nav-item ${activePath === '/' ? 'active' : ''}`}
            onClick={() => handleNavigate('/')}
            title="Home"
          >
            <MdHome size={24} />
            <span>Home</span>
          </button>
          <button
            type="button"
            className={`yt-mobile-bottom-nav-item ${activePath.startsWith('/shorts') ? 'active' : ''}`}
            onClick={() => handleNavigate('/shorts')}
            title="Shorts"
          >
            <SiYoutubekids size={24} />
            <span>Shorts</span>
          </button>
          <button
            type="button"
            className="yt-mobile-bottom-nav-item create-btn"
            onClick={() => {
              setUploadModalVideoType('video');
              setIsUploadModalOpen(true);
            }}
            title="Create"
          >
            <div className="yt-mobile-create-icon-wrap">
              <MdAdd size={24} />
            </div>
          </button>
          <button
            type="button"
            className={`yt-mobile-bottom-nav-item ${activePath === '/albums' ? 'active' : ''}`}
            onClick={() => handleNavigate('/albums')}
            title="Albums"
          >
            <MdOutlineAlbum size={24} />
            <span>Albums</span>
          </button>
          <button
            type="button"
            className={`yt-mobile-bottom-nav-item ${(activePath === '/history' || activePath === '/profile') ? 'active' : ''}`}
            onClick={() => handleNavigate(isLoggedIn ? '/history' : '/')}
            title="You"
          >
            <HiOutlineUser size={24} />
            <span>You</span>
          </button>
        </nav>
      )}

      {/* Global Toast Notification (Bottom Left Corner) */}
      <Toast
        isOpen={globalToast.isOpen}
        message={globalToast.message}
        icon={globalToast.icon}
        toastKey={globalToast.toastKey}
        duration={4500}
        onClose={() => setGlobalToast({ isOpen: false, message: '', icon: null, toastKey: null })}
      />
    </div>
  );
}
